"use client"

import React, { useState } from "react"

import { cn } from "@/lib/utils"
import { Flag, RemixIcon, Typography } from "@/components/ui/atoms"

type TTextInputProps = {
  id: string
  className?: string
  label?: string
  iconLeft?: string
  iconRight?: string
  currencyValue: "CHF" | "EUR"
  version?: "menuVersion" | "smallVersion" | "heroVersion"
  additionalText?: string
  characterLimit?: number
  disabled?: boolean
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const TextInput = ({
  label,
  id,
  className,
  iconLeft,
  iconRight,
  currencyValue,
  additionalText,
  characterLimit,
  version = "heroVersion",
  disabled,
  value,
  ...props
}: TTextInputProps) => {
  const [charCount, setCharCount] = useState(0) // State to track character count
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (characterLimit && inputValue.length <= characterLimit) {
      setCharCount(inputValue.length)
    } else {
      e.target.value = inputValue.substring(0, characterLimit)
    }
  }

  /* Throw error if both iconRight and Currency parameters are set*/
  if (iconRight && currencyValue) {
    throw new Error(
      "The component can contain either an icon or a currency parameter on the right side."
    )
  }

  return (
    <div>
      <div
        className={cn(
          "text-input relative rounded-400 bg-neutral-200 px-5 py-4",
          className
        )}
      >
        {label ? (
          <label
            className={cn("absolute left-[2em] top-[0.5em]", className)}
            htmlFor={id}
          >
            <Typography
              as="p"
              variant="labelS"
              className="mb-6 text-neutral-400 md:mb-7"
            >
              {label}
            </Typography>
          </label>
        ) : null}
        {iconLeft ? (
          <RemixIcon
            name={iconLeft}
            size="sm"
            className="absolute left-[0.5em] top-[1em]"
          />
        ) : null}
        <input
          onChange={handleInput}
          value={value}
          className={cn(
            label
              ? "translate-x-[1rem] translate-y-[0.5rem]"
              : "translate-x-[1rem]",
            iconLeft ? null : "transform-none"
          )}
          id={id}
          {...props}
          disabled={disabled}
        />
        {iconRight ? (
          <RemixIcon
            name={iconRight}
            size="sm"
            className="absolute right-[0.75em] top-[1em]"
          />
        ) : null}
        {currencyValue ? (
          <div className="input-currency absolute right-[0.75em] top-[1em] flex">
            <Typography
              as="p"
              variant="labelS"
              className="mb-6 mr-2 text-neutral-500 md:mb-7"
            >
              {currencyValue}
            </Typography>
            <Flag country={currencyValue} />
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex justify-between">
        {additionalText ? (
          <Typography
            as="p"
            variant="labelS"
            className="mb-6 text-neutral-400 md:mb-7"
          >
            {additionalText}
          </Typography>
        ) : null}
        {characterLimit ? (
          <Typography
            as="p"
            variant="labelS"
            className="mb-6 text-neutral-400 md:mb-7"
          >
            {charCount} / {characterLimit}
          </Typography>
        ) : null}
      </div>
    </div>
  )
}

export { TextInput, type TTextInputProps }
